import type { WorkflowGraph } from '../../api/workflowRuns'
import { isGarbledWorkflowText, workflowInputFieldDefaults } from './workflowDefinitionDisplay'
import { workflowTypeFromGraph } from './workflowTemplates'

export type WorkflowInputFieldType = 'text' | 'textarea' | 'number' | 'select'

export type WorkflowInputFieldOption = {
  value: string
  label: string
  description?: string
}

export type WorkflowInputField = {
  key: string
  label: string
  type: WorkflowInputFieldType
  required?: boolean
  placeholder?: string
  defaultValue?: string | number
  options?: WorkflowInputFieldOption[]
}

const INPUT_FIELD_LABELS: Record<string, string> = {
  story: '故事 / 剧情',
  prompt: '提示词',
  pageCount: '分页数量',
}

function labelForInputKey(key: string) {
  return INPUT_FIELD_LABELS[key] ?? key
}

function resolveInputFieldMeta(
  key: string,
  row: Record<string, unknown>,
  graph?: WorkflowGraph | null,
): { label: string; placeholder: string } {
  const rawLabel = String(row.label ?? '').trim()
  const rawPlaceholder = String(row.placeholder ?? '').trim()
  const workflowType = workflowTypeFromGraph(graph ?? null)
  const defaults = workflowInputFieldDefaults(key, workflowType)
  const defaultLabel = defaults.label ?? labelForInputKey(key)
  const defaultPlaceholder = defaults.placeholder ?? ''
  return {
    label: !rawLabel || isGarbledWorkflowText(rawLabel) ? defaultLabel : rawLabel,
    placeholder: !rawPlaceholder || isGarbledWorkflowText(rawPlaceholder) ? defaultPlaceholder : rawPlaceholder,
  }
}

export function inferWorkflowInputFields(graph: WorkflowGraph | null | undefined): WorkflowInputField[] {
  const schema = (graph?.inputSchema ?? {}) as { fields?: unknown }
  const rawFields = Array.isArray(schema.fields) ? schema.fields : []
  const fields: WorkflowInputField[] = rawFields
    .map((item): WorkflowInputField | null => {
      if (!item || typeof item !== 'object') return null
      const row = item as Record<string, unknown>
      const key = String(row.key ?? '').trim()
      if (!key) return null
      const typeRaw = String(row.type ?? 'text')
      const type: WorkflowInputFieldType =
        typeRaw === 'textarea' || typeRaw === 'number' || typeRaw === 'select' ? typeRaw : 'text'
      const { label, placeholder } = resolveInputFieldMeta(key, row, graph)
      return {
        key,
        label,
        type,
        required: Boolean(row.required),
        placeholder,
        defaultValue:
          typeof row.defaultValue === 'number' || typeof row.defaultValue === 'string'
            ? (row.defaultValue as string | number)
            : undefined,
        options: Array.isArray(row.options)
          ? row.options
              .map((item) => {
                if (!item || typeof item !== 'object') return null
                const option = item as Record<string, unknown>
                const value = String(option.value ?? '').trim()
                if (!value) return null
                return {
                  value,
                  label: String(option.label ?? value).trim() || value,
                  description: String(option.description ?? '').trim() || undefined,
                }
              })
              .filter(Boolean) as WorkflowInputFieldOption[]
          : undefined,
      }
    })
    .filter(Boolean) as WorkflowInputField[]
  if (fields.length > 0) return fields
  return [
    {
      key: 'input',
      label: '输入',
      type: 'textarea',
      required: true,
      placeholder: '输入工作流运行内容',
    },
  ]
}

export function buildRunInputPayload(
  fields: WorkflowInputField[],
  values: Record<string, string>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  for (const field of fields) {
    const raw = values[field.key]?.trim() ?? ''
    if (!raw) {
      if (field.defaultValue !== undefined) payload[field.key] = field.defaultValue
      continue
    }
    if (field.type === 'number') {
      const parsed = Number(raw)
      payload[field.key] = Number.isNaN(parsed) ? field.defaultValue ?? raw : parsed
      continue
    }
    payload[field.key] = raw
  }
  return payload
}

export function defaultInputValues(fields: WorkflowInputField[]): Record<string, string> {
  const values: Record<string, string> = {}
  for (const field of fields) {
    if (field.defaultValue !== undefined) values[field.key] = String(field.defaultValue)
    else values[field.key] = ''
  }
  return values
}
